import React, { useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useSuperAuth } from '../../contexts/SuperAuthContext';
import { getApiBase, getPublicApiOrigin } from '../../lib/api';
import { toast } from 'sonner';
import { Loader2, Shield } from 'lucide-react';

export default function SuperLogin() {
  const { user, ready, loginStep1, verifyTotp } = useSuperAuth();
  const [step, setStep] = useState('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const [otpauthUri, setOtpauthUri] = useState('');
  const [loading, setLoading] = useState(false);

  if (ready && user) return <Navigate to="/super" replace />;

  const submitCredentials = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await loginStep1(email, password);
      if (result.done) {
        toast.success('Sesión iniciada');
        return;
      }
      setMfaToken(result.mfaToken);
      setOtpauthUri(result.otpauthUri || '');
      setStep(result.setup ? 'setup' : 'totp');
      if (result.setup) toast.info('Escanea el código QR con Google Authenticator');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Credenciales inválidas');
    } finally {
      setLoading(false);
    }
  };

  const submitTotp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verifyTotp(mfaToken, code);
      toast.success('Autenticación completada');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Código incorrecto');
    } finally {
      setLoading(false);
    }
  };

  const origin = getPublicApiOrigin() || '';
  const qrUrl = otpauthUri
    ? `${origin}${getApiBase()}/auth/totp/qr?uri=${encodeURIComponent(otpauthUri)}`
    : '';

  const inputClass =
    'mt-1 w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500';
  const btnClass =
    'w-full inline-flex items-center justify-center gap-2 rounded-md bg-teal-600 hover:bg-teal-500 text-white text-sm font-medium py-2.5 disabled:opacity-50';

  return (
    <div className="min-h-screen relative grid place-items-center px-4 bg-slate-950">
      <div className="relative w-full max-w-md p-8 rounded-lg border border-slate-800 bg-slate-900/90 shadow-xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-12 w-12 grid place-items-center bg-teal-500 text-slate-950 rounded-md">
            <Shield size={24} />
          </div>
          <div>
            <div className="text-2xl font-semibold text-white">Super Admin</div>
            <div className="text-xs text-slate-400 uppercase tracking-widest">Plataforma · 2FA</div>
          </div>
        </div>

        {step === 'credentials' && (
          <form onSubmit={submitCredentials} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400">Email</label>
              <input type="email" className={inputClass} value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400">Contraseña</label>
              <input type="password" className={inputClass} value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            <button type="submit" disabled={loading} className={btnClass}>
              {loading ? <Loader2 className="animate-spin" size={16} /> : 'Continuar'}
            </button>
          </form>
        )}

        {(step === 'setup' || step === 'totp') && (
          <form onSubmit={submitTotp} className="space-y-4">
            {step === 'setup' && qrUrl && (
              <div className="text-center">
                <p className="text-xs text-slate-400 mb-3">Escanea con Google Authenticator</p>
                <img src={qrUrl} alt="QR 2FA" className="mx-auto bg-white p-2 rounded-md max-w-[200px]" />
              </div>
            )}
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400">Código de 6 dígitos</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                className={`${inputClass} text-center text-2xl tracking-[0.5em]`}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
              />
            </div>
            <button type="submit" disabled={loading || code.length !== 6} className={btnClass}>
              {loading ? <Loader2 className="animate-spin" size={16} /> : 'Verificar'}
            </button>
            <button
              type="button"
              className="text-xs text-slate-400 w-full"
              onClick={() => {
                setStep('credentials');
                setCode('');
              }}
            >
              Volver
            </button>
          </form>
        )}

        <div className="mt-6 space-y-2 text-center text-sm">
          <Link to="/" className="block text-slate-400 hover:text-teal-400">
            Sitio público
          </Link>
          <Link to="/admin/login" className="block text-slate-400 hover:text-white">
            Panel de la tienda
          </Link>
        </div>
      </div>
    </div>
  );
}
